/**
 * TimezoneService handles timezone detection and management for the browser app
 */
class TimezoneService {
  constructor() {
    this.userTimezone = null;
    this.initialize();
  }

  /**
   * Initialize the timezone service
   */
  initialize() {
    this.detectUserTimezone();
  }

  /**
   * Detect the user's timezone using browser APIs
   */
  detectUserTimezone() {
    try {
      // Use Intl.DateTimeFormat to get the user's timezone
      this.userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log("Detected user timezone:", this.userTimezone);
    } catch (error) {
      console.warn("Failed to detect timezone, falling back to EST:", error);
      // Fallback to EST (Eastern Time) as mentioned by the user
      this.userTimezone = "America/New_York";
    }
  }

  /**
   * Get the user's timezone
   * @returns {string} The user's timezone identifier
   */
  getUserTimezone() {
    return this.userTimezone || "America/New_York";
  }

  /**
   * Get timezone offset information
   * @returns {object} Timezone offset information
   */
  getTimezoneOffset() {
    const now = new Date();
    const offset = now.getTimezoneOffset();

    return {
      offset: offset,
      offsetHours: offset / 60,
      offsetString: this.formatOffset(offset),
      timezone: this.getUserTimezone(),
    };
  }

  /**
   * Format timezone offset as a string (e.g., "-05:00")
   * @param {number} offsetMinutes - Offset in minutes
   * @returns {string} Formatted offset string
   */
  formatOffset(offsetMinutes) {
    const hours = Math.floor(Math.abs(offsetMinutes) / 60);
    const minutes = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes <= 0 ? "+" : "-";

    return `${sign}${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  }

  /**
   * Convert a date to the user's timezone
   * @param {Date|string} date - Date to convert
   * @returns {Date} Date in user's timezone
   */
  convertToUserTimezone(date) {
    const dateObj = new Date(date);
    return new Date(
      dateObj.toLocaleString("en-US", { timeZone: this.getUserTimezone() })
    );
  }

  /**
   * Format a date in the user's timezone
   * @param {Date|string} date - Date to format
   * @param {object} options - Intl.DateTimeFormat options
   * @returns {string} Formatted date string
   */
  formatInUserTimezone(date, options = {}) {
    const dateObj = new Date(date);
    const defaultOptions = {
      timeZone: this.getUserTimezone(),
      ...options,
    };

    return dateObj.toLocaleString("en-US", defaultOptions);
  }

  /**
   * Get timezone parameters for API requests
   * @returns {object} Timezone parameters for API calls
   */
  getApiTimezoneParams() {
    return {
      timezone: this.getUserTimezone(),
      timezone_offset: this.getTimezoneOffset().offsetString,
    };
  }

  /**
   * Add timezone parameters to a URL
   * @param {string} url - Base URL
   * @returns {string} URL with timezone parameters
   */
  addTimezoneToUrl(url) {
    const params = this.getApiTimezoneParams();
    const urlObj = new URL(url);

    Object.keys(params).forEach((key) => {
      urlObj.searchParams.set(key, params[key]);
    });

    return urlObj.toString();
  }

  /**
   * Add timezone parameters to request data
   * @param {object} data - Request data object
   * @returns {object} Data with timezone parameters added
   */
  addTimezoneToData(data = {}) {
    return {
      ...data,
      ...this.getApiTimezoneParams(),
    };
  }
}

// Create and export a singleton instance
const timezoneService = new TimezoneService();
export default timezoneService;
